import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import type { TalkingPoint } from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { MeetingPrepCardHideReason } from "@acto/gql/graphql";
import {
  useHideMeetingPrepCard,
  useRestoreMeetingPrepCard,
  useRevealMeetingPrepCardOnce,
  useUndoMeetingPrepCardHide,
} from "@acto/taskpane/core/data-access/meeting-prep-card-hides";
import { useRestoreMeetingPrepLearnedDefault } from "@acto/taskpane/core/data-access/meeting-prep-learned-defaults";
import type { MeetingPrepCardHide, MeetingPrepLearnedDefault } from "@acto/taskpane/core/domain";
import { AnalyticsEvents, useTrack, type MeetingPrepCardHiddenProperties } from "@acto/taskpane/core/analytics";
import { MoreButton } from "@acto/taskpane/field-sales-2/components/primitives/MoreButton";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { useUndoBatch } from "@acto/taskpane/field-sales-2/utils/hooks/useUndoBatch";
import { ProductHideFlow } from "@acto/taskpane/field-sales-2/components/patterns/product-hide";
import { hideableProductsOf, isProductHideable } from "@acto/taskpane/field-sales-2/utils/productHide";
import { ShellOverlay } from "@acto/taskpane/field-sales-2/shell";
import { shellBarsScrollMargin } from "@acto/taskpane/field-sales-2/styles/shellBarsScrollMargin";
import { overlaidMenuClearance } from "@acto/taskpane/field-sales-2/styles/overlaidMenuClearance";
import { talkingPointLabel } from "./cardLabel";
import { ReasonPrompt, TRACKED_REASON_BY_KIND, type HideReasonChoice, type HideReasonKind } from "./ReasonPrompt";
import { HiddenCardNotice } from "./HiddenCardNotice";
import { HiddenCardLearnedRule, HiddenCardOneOff } from "./HiddenCardCollapsed";

const REASON_BY_KIND: Record<HideReasonKind, MeetingPrepCardHideReason> = {
  contentNotRelevant: MeetingPrepCardHideReason.ContentNotRelevant,
  dataIncorrect: MeetingPrepCardHideReason.DataIncorrect,
};

type HideResult = { id: string } | null | undefined;

interface ScheduledHide {
  result: Promise<HideResult>;
  hiddenEvent: MeetingPrepCardHiddenProperties;
}

interface PendingHide extends ScheduledHide {
  kind: HideReasonKind;
}

type Phase = "idle" | "reason" | "adjustProducts";

const CardShell = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  "& header": {
    paddingRight: overlaidMenuClearance({
      menuRightInset: theme.fieldSalesTwo.spacing[16],
      menuButtonWidth: theme.fieldSalesTwo.spacing[50],
      gapToContent: theme.fieldSalesTwo.spacing[8],
      cardPaddingX: theme.fieldSalesTwo.spacing[20],
    }),
  },
}));

const MenuOverlay = styled("div")(({ theme }) => ({
  position: "absolute",
  top: theme.fieldSalesTwo.spacing[16],
  right: theme.fieldSalesTwo.spacing[16],
  zIndex: 1,
  "& > button": shellBarsScrollMargin(theme),
}));

interface HidableTalkingPointProps {
  point: TalkingPoint;
  clientId: string;
  cardHide?: { prepSessionId: string; cardKey: string };
  /** The ACTIVE hide that masks this card for the viewer, if any. */
  activeHide?: MeetingPrepCardHide;
  learnedDefault?: MeetingPrepLearnedDefault;
  /** True while the undo notice is shown or the card is revealed by "Show once"; the card keeps its slot meanwhile. */
  onSettling?: (settling: boolean) => void;
  renderCard: () => ReactNode;
}

export function HidableTalkingPoint({
  point,
  clientId,
  cardHide,
  activeHide,
  learnedDefault,
  onSettling,
  renderCard,
}: HidableTalkingPointProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.cardHide");
  const tProducts = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const [phase, setPhase] = useState<Phase>("idle");
  const [pendingHide, setPendingHide] = useState<PendingHide | null>(null);
  const [isRevealedOnce, setIsRevealedOnce] = useState(false);
  const { hideMeetingPrepCard } = useHideMeetingPrepCard();
  const { restoreMeetingPrepCard } = useRestoreMeetingPrepCard();
  const { undoMeetingPrepCardHide } = useUndoMeetingPrepCardHide();
  const { revealMeetingPrepCardOnce } = useRevealMeetingPrepCardOnce();
  const { restoreMeetingPrepLearnedDefault } = useRestoreMeetingPrepLearnedDefault();
  const track = useTrack();
  // Undo can land after the window has committed, while a slow hide request is still open.
  const undoneHides = useRef(new WeakSet<Promise<HideResult>>());
  const cardLabel = talkingPointLabel(point, t);
  const canAdjustProducts = isProductHideable(point);

  // Read through a ref: the parent passes a fresh closure each render, and re-running the effect
  // on identity changes would flip the parent's state back and forth.
  const onSettlingRef = useRef(onSettling);
  useEffect(() => {
    onSettlingRef.current = onSettling;
  });
  const settling = pendingHide !== null || isRevealedOnce;
  // A layout effect: the report must reach the consumer before the browser paints the change.
  useLayoutEffect(() => {
    onSettlingRef.current?.(settling);
    return () => onSettlingRef.current?.(false);
  }, [settling]);

  // The mutation is sent when the reason is submitted; the undo window only decides whether the
  // notice gives way to the collapsed row. Settling waits for the request so a slow answer cannot
  // flash the card back in between. Tracking waits for the same window.
  const undoWindow = useUndoBatch<ScheduledHide>((batch) => {
    for (const { result, hiddenEvent } of batch.values()) {
      void result.then((hide) => {
        setPendingHide((current) => (current?.result === result ? null : current));
        if (!hide) return;
        setIsRevealedOnce(false);
        if (!undoneHides.current.has(result)) track(AnalyticsEvents.MEETING_PREP_CARD_HIDDEN, hiddenEvent);
      });
    }
  });

  const handleReasonSelect = (choice: HideReasonChoice) => {
    if (!cardHide) return;

    const result = hideMeetingPrepCard({
      clientId,
      prepSessionId: cardHide.prepSessionId,
      cardKey: cardHide.cardKey,
      cardType: point.type,
      reason: REASON_BY_KIND[choice.kind],
      reasonText: choice.text,
      card: JSON.stringify(point),
    });
    const pending: PendingHide = {
      kind: choice.kind,
      result,
      hiddenEvent: {
        customerId: clientId,
        cardType: point.type,
        reason: TRACKED_REASON_BY_KIND[choice.kind],
        reasonText: choice.text,
        countsTowardThreshold: choice.kind === "contentNotRelevant",
      },
    };
    setPhase("idle");
    setPendingHide(pending);
    undoWindow.schedule(cardHide.cardKey, pending);

    void result.then((hide) => {
      // A failed hide leaves the card in place.
      if (hide === null) {
        undoWindow.undo();
        setPendingHide((current) => (current?.result === result ? null : current));
      }
    });
  };

  const handleUndo = async () => {
    if (!pendingHide || !cardHide) return;
    undoWindow.undo();
    undoneHides.current.add(pendingHide.result);
    const hide = await pendingHide.result;
    if (hide) {
      const undone = await undoMeetingPrepCardHide(hide.id);
      if (undone === null) {
        // The undo failed and the hide stands: it is re-armed to commit and be tracked unless a retry succeeds.
        undoneHides.current.delete(pendingHide.result);
        undoWindow.schedule(cardHide.cardKey, pendingHide);
        return;
      }
    }
    setPendingHide(null);
  };

  const trackUnhidden = (unhideType: "always_show" | "just_once") =>
    track(AnalyticsEvents.MEETING_PREP_CARD_UNHIDDEN, { customerId: clientId, cardType: point.type, unhideType });

  const handleAlwaysShow = async () => {
    if (!learnedDefault) return;
    const restored = await restoreMeetingPrepLearnedDefault(learnedDefault.id);
    if (restored) trackUnhidden("always_show");
  };

  const handleUnhide = async () => {
    if (!activeHide) return;
    const restored = await restoreMeetingPrepCard(activeHide.id);
    if (restored) trackUnhidden("always_show");
  };

  // A reveal that fails to store is dropped silently, so the card collapses again on the next mount.
  const handleShowOnce = () => {
    setIsRevealedOnce(true);
    trackUnhidden("just_once");
    if (cardHide) void revealMeetingPrepCardOnce({ clientId, ...cardHide });
  };

  if (pendingHide) {
    return <HiddenCardNotice cardLabel={cardLabel} reason={pendingHide.kind} onUndo={() => void handleUndo()} />;
  }

  switch (phase) {
    case "reason":
      return (
        <ReasonPrompt
          question={t("reasonQuestion", { label: cardLabel })}
          onSelect={handleReasonSelect}
          onCancel={() => setPhase("idle")}
        />
      );
    case "adjustProducts":
      return (
        <ShellOverlay id="productHideAdjust" label={cardLabel} onDismiss={() => setPhase("idle")}>
          <ProductHideFlow
            products={hideableProductsOf(point)}
            talkingPointLabel={cardLabel}
            onBack={() => setPhase("idle")}
            clientId={clientId}
          />
        </ShellOverlay>
      );
    case "idle": {
      if (!isRevealedOnce && activeHide) {
        return <HiddenCardOneOff cardLabel={cardLabel} onUnhide={() => void handleUnhide()} />;
      }
      if (!isRevealedOnce && learnedDefault) {
        return (
          <HiddenCardLearnedRule
            cardLabel={cardLabel}
            hint={t("learnedRuleHint", { count: learnedDefault.learnedAfterHides })}
            onAlwaysShow={() => void handleAlwaysShow()}
            onShowOnce={handleShowOnce}
          />
        );
      }
      const menuItems = [
        ...(cardHide ? [{ label: t("hide"), onSelect: () => setPhase("reason") }] : []),
        ...(canAdjustProducts ? [{ label: tProducts("menuAdjust"), onSelect: () => setPhase("adjustProducts") }] : []),
      ];
      if (menuItems.length === 0) return <>{renderCard()}</>;
      return (
        <CardShell>
          {renderCard()}
          <MenuOverlay>
            <MenuPopover
              trigger={<MoreButton aria-label={t("moreOptions")} />}
              align="end"
              surface="list"
              ariaLabel={t("moreOptions")}
              items={menuItems}
            />
          </MenuOverlay>
        </CardShell>
      );
    }
  }
}
